import { CaretRightOutlined, PlusOutlined } from '@ant-design/icons';
import type { CollapseProps, UploadFile, UploadProps } from 'antd';
import { Collapse, Form, Image, Input, Switch, Upload } from 'antd';
import { Select } from 'components';
import { UserFormField } from 'features/users/components/users-filter-form';
import { useGetRoleList } from 'features/users/use-cases/get-role';
import { useUserContext } from 'features/users/user-context';
import { t } from 'i18next';
import { Fragment, useEffect } from 'react';
import { validateImageBeforeUpload } from 'routes/utils';
import { CSSProperties } from 'styled-components';

export const UserInfoFormItems = ({
  fileList,
  setFileList,
  previewImage,
  setPreviewImage,
  previewOpen,
  setPreviewOpen,
  Id,
  getUsersListQueryResponse,
  categoryChecks,
  setCategoryChecks,
  subCategoryChecks,
  setSubCategoryChecks,
  classCategoryChecks,
  setClassCategoryChecks,
}: any) => {
  const { user } = useUserContext();
  const { data } = useGetRoleList({
    UserType: 2,
    CompanyId: user?.CompanyId,
  } as any);
  const handlePreview = async (file: UploadFile) => {
    setPreviewImage(file.url || (file.preview as string));
    setPreviewOpen(true);
  };
  const handleChange: UploadProps['onChange'] = ({ fileList: newFileList }) =>
    setFileList(newFileList);
  const uploadButton = (
    <button style={{ border: 0, background: 'none' }} type='button'>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </button>
  );
  useEffect(() => {
    if (getUsersListQueryResponse?.data?.data) {
      const initialCategoryChecks: Record<string, boolean> = {};
      const initialSubCategoryChecks: Record<string, boolean> = {};
      const initialClassCategoryChecks: Record<string, boolean> = {};

      getUsersListQueryResponse?.data?.data.rc.forEach((category: any) => {
        initialCategoryChecks[category.Id] = category.IsChecked;

        category.SubCategoryList.forEach((subCategory: any) => {
          initialSubCategoryChecks[subCategory.Id] = subCategory.IsChecked;

          subCategory.ClassCategoryList.forEach((classCategory: any) => {
            initialClassCategoryChecks[classCategory.Id] =
              classCategory.IsChecked;
          });
        });
      });

      setCategoryChecks(initialCategoryChecks);
      setSubCategoryChecks(initialSubCategoryChecks);
      setClassCategoryChecks(initialClassCategoryChecks);
    }
  }, [getUsersListQueryResponse?.data?.data]);
  const handleCategorySwitchChange = (
    checked: boolean,
    categoryId: string,
    subCategories: any[],
  ) => {
    setCategoryChecks((prev: any) => ({ ...prev, [categoryId]: checked }));

    const updatedSubCategoryChecks = { ...subCategoryChecks };
    const updatedClassCategoryChecks = { ...classCategoryChecks };

    subCategories.forEach((subCategory) => {
      updatedSubCategoryChecks[subCategory.Id] = checked;
      subCategory.ClassCategoryList.forEach((classCategory: any) => {
        updatedClassCategoryChecks[classCategory.Id] = checked;
      });
    });

    setSubCategoryChecks(updatedSubCategoryChecks);
    setClassCategoryChecks(updatedClassCategoryChecks);
  };

  const handleSubCategorySwitchChange = (
    checked: boolean,
    subCategoryId: string,
    classCategories: any[],
  ) => {
    setSubCategoryChecks((prev: any) => ({
      ...prev,
      [subCategoryId]: checked,
    }));

    const updatedClassCategoryChecks = { ...classCategoryChecks };
    classCategories.forEach((classCategory: any) => {
      updatedClassCategoryChecks[classCategory.Id] = checked;
    });

    setClassCategoryChecks(updatedClassCategoryChecks);
  };
  const getItems: (panelStyle1: CSSProperties) => CollapseProps['items'] = (
    panelStyle1,
  ) =>
    getUsersListQueryResponse?.data?.data?.rc.map((category: any) => ({
      key: String(category.Id),
      label: (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>{category.CategoryName}</span>
          <Switch
            checked={categoryChecks[category.Id] || false}
            onChange={(checked) =>
              handleCategorySwitchChange(
                checked,
                category.Id,
                category.SubCategoryList,
              )
            }
          />
        </div>
      ),
      children: (
        <div>
          {category.SubCategoryList.map((subCategory: any) => (
            <div
              key={subCategory.Id}
              style={{ marginLeft: '20px', marginBottom: '10px' }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span>{subCategory.SubCategory}</span>
                <Switch
                  checked={subCategoryChecks[subCategory.Id] || false}
                  onChange={(checked) =>
                    handleSubCategorySwitchChange(
                      checked,
                      subCategory.Id,
                      subCategory.ClassCategoryList,
                    )
                  }
                />
              </div>

              {/* Class categories */}
              {subCategory.ClassCategoryList.length > 0 && (
                <div style={{ marginLeft: '20px', marginTop: '10px' }}>
                  {subCategory.ClassCategoryList.map((classCategory: any) => (
                    <div
                      key={classCategory.Id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <span>{classCategory.ClassCategory}</span>
                      <Switch
                        checked={classCategoryChecks[classCategory.Id] || false}
                        onChange={(checked) =>
                          setClassCategoryChecks((prev: any) => ({
                            ...prev,
                            [classCategory.Id]: checked,
                          }))
                        }
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      ),
      style: panelStyle1,
    }));

  return (
    <Fragment>
      <Form.Item<UserFormField>
        name={'Name'}
        label={t('usermanagement.formFields.userName.label')}
        rules={[
          {
            required: true,
            whitespace: false,
          },
        ]}
      >
        <Input
          placeholder={t('usermanagement.formFields.userName.placeholder')}
        />
      </Form.Item>
      <Form.Item<UserFormField>
        label={t('usermanagement.formFields.email.label')}
        name={UserFormField.Email}
        rules={[
          {
            required: true,
            whitespace: false,
          },
        ]}
      >
        <Input placeholder={t('usermanagement.formFields.email.placeholder')} />
      </Form.Item>
      <Form.Item
        label='Send Mail'
        name='SendMailAccess'
        valuePropName='checked'
      >
        <Switch />
      </Form.Item>
      <Form.Item<UserFormField>
        label={t('usermanagement.formFields.role.label')}
        name={UserFormField.Role}
        rules={[
          {
            required: true,
            whitespace: false,
          },
        ]}
      >
        <Select
          options={data?.rc?.map((item: any) => ({
            value: item.Id,
            label: item.RoleName,
          }))}
          placeholder={t('usermanagement.formFields.role.placeholder')}
        />
      </Form.Item>
      {!Id && (
        <Form.Item
          label={t('usermanagement.formFields.profile.label')}
          name={UserFormField.UserProfile}
        >
          <div style={{ width: '100%' }}>
            <Upload
              action='https://660d2bd96ddfa2943b33731c.mockapi.io/api/upload'
              listType='picture-card'
              fileList={fileList}
              onPreview={handlePreview}
              onChange={handleChange}
              beforeUpload={validateImageBeforeUpload}
              maxCount={1}
              style={{ width: '100%' }} // Ensure the Upload takes 100% width
            >
              {fileList?.length >= 1 ? null : (
                <div style={{ width: '100%' }}>
                  {' '}
                  {/* Upload button full width */}
                  {uploadButton}
                </div>
              )}
            </Upload>
          </div>
          {previewImage && (
            <Image
              wrapperStyle={{ display: 'none' }}
              preview={{
                visible: previewOpen,
                onVisibleChange: (visible) => setPreviewOpen(visible),
                afterOpenChange: (visible) => !visible && setPreviewImage(''),
              }}
              src={previewImage}
            />
          )}
        </Form.Item>
      )}
      <Collapse
        bordered={false}
        defaultActiveKey={[1]}
        expandIcon={({ isActive }) => (
          <CaretRightOutlined rotate={isActive ? 90 : 0} />
        )}
        // style={{ background: token.colorBgContainer }}
        items={getItems({})}
      />
    </Fragment>
  );
};
